import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Shield, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { insuranceTypes } from './types/insurance';
import { Hero } from './components/Hero';
import { WhyChooseUs } from './components/WhyChooseUs';
import { useScrollToTop } from './hooks/useScrollToTop';
import { submitContactRequest } from './lib/supabase';
import { Logo } from './components/Logo';
import { InsuranceForm } from './components/InsuranceForm';
import { PersonalInfoForm } from './components/PersonalInfoForm';

// Lazy load less critical components
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Blog = lazy(() => import('./pages/Blog').then(module => ({ default: module.Blog })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const CallMe = lazy(() => import('./pages/CallMe').then(module => ({ default: module.CallMe })));

type Page = 'home' | 'form' | 'personal' | 'privacy' | 'terms' | 'about' | 'contact' | 'blogs' | 'call-me';

function App() {
  const [selectedInsurances, setSelectedInsurances] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<Page>('home');

  useScrollToTop(currentPage);

  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      const page = getPageFromPath(path);
      setCurrentPage(page);
      document.title = getPageTitle(page);
    };

    handleNavigation();
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  const getPageFromPath = (path: string): Page => {
    if (path.startsWith('/blog/')) {
      return 'blogs';
    }
    
    const pathMap: { [key: string]: Page } = {
      '/': 'home',
      '/about': 'about',
      '/contact': 'contact',
      '/blogs': 'blogs',
      '/privacy': 'privacy',
      '/terms': 'terms',
      '/ring-mig-op': 'call-me'
    };
    return pathMap[path] || 'home';
  };

  const getPageTitle = (page: Page): string => {
    const baseTitle = 'AutoInsurance.dk';
    const titles: { [key in Page]: string } = {
      home: `Billig Forsikring & Gratis Forsikringstjek 2025 | ${baseTitle}`,
      form: `Billig Forsikring & Gratis Forsikringstjek 2025 | ${baseTitle}`,
      personal: `Billig Forsikring & Gratis Forsikringstjek 2025 | ${baseTitle}`,
      about: `Om os | ${baseTitle}`,
      contact: `Kontakt | ${baseTitle}`,
      blogs: `Blog & Guides | ${baseTitle}`,
      privacy: `Persondatapolitik | ${baseTitle}`,
      terms: `Vilkår | ${baseTitle}`,
      'call-me': `Ring mig op | ${baseTitle}`
    };
    return titles[page];
  };

  const navigateToPage = (page: Page) => {
    const pathMap: { [key in Page]: string } = {
      home: '/',
      form: '/',
      personal: '/',
      about: '/about',
      contact: '/contact',
      blogs: '/blogs',
      privacy: '/privacy',
      terms: '/terms',
      'call-me': '/ring-mig-op'
    };

    const path = pathMap[page];
    window.history.pushState({}, '', path);
    setCurrentPage(page);
    document.title = getPageTitle(page);
  };

  const toggleInsurance = (id: string) => {
    setSelectedInsurances(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const renderContent = () => {
    const content = (() => {
      switch (currentPage) {
        case 'form':
          return (
            <InsuranceForm 
              selectedInsurances={selectedInsurances}
              onBack={() => navigateToPage('home')}
              onComplete={() => navigateToPage('personal')}
            />
          );
        case 'personal':
          return (
            <PersonalInfoForm 
              selectedInsurances={selectedInsurances}
              onBack={() => navigateToPage('form')}
            />
          );
        case 'privacy':
          return <PrivacyPolicy />;
        case 'terms':
          return <Terms />;
        case 'about':
          return <About />;
        case 'contact':
          return <Contact />;
        case 'blogs':
          return <Blog />;
        case 'call-me':
          return <CallMe />;
        default:
          return (
            <>
              <Hero navigateToCallMe={() => navigateToPage('call-me')} />
              <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <motion.div 
                  id="insurance-section" 
                  className="bg-white rounded-2xl shadow-xl p-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                    Hvilken forsikring skal vi hjælpe dig med?
                  </h2>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {insuranceTypes.map((insurance, index) => (
                      <motion.button
                        key={insurance.id}
                        onClick={() => toggleInsurance(insurance.id)}
                        className={`relative flex flex-col items-center p-6 rounded-xl border-2 transition-all
                          ${selectedInsurances.includes(insurance.id)
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-green-200 hover:bg-gray-50'
                          }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {selectedInsurances.includes(insurance.id) && (
                          <motion.div 
                            className="absolute top-2 right-2"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                            <div className="bg-green-500 rounded-full p-1">
                              <X className="w-4 h-4 text-white" />
                            </div>
                          </motion.div>
                        )}
                        {React.createElement(
                          (Icons as any)[insurance.icon],
                          {
                            className: `w-8 h-8 mb-3 ${
                              selectedInsurances.includes(insurance.id) ? 'text-green-500' : 'text-gray-600'
                            }`
                          }
                        )}
                        <span className={`text-sm text-center ${
                          selectedInsurances.includes(insurance.id) ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {insurance.name}
                        </span>
                        {insurance.popular && (
                          <motion.span 
                            className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                          >
                            Populær
                          </motion.span>
                        )}
                      </motion.button>
                    ))}
                  </div>

                  <motion.div 
                    className="mt-8 flex justify-between items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p className="text-sm text-gray-500">
                      Du har valgt {selectedInsurances.length} ud af {insuranceTypes.length} forsikringstyper
                    </p>
                    <motion.button
                      onClick={() => navigateToPage('form')}
                      disabled={selectedInsurances.length === 0}
                      className={`px-6 py-3 rounded-lg font-medium transition-all
                        ${selectedInsurances.length > 0
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      whileHover={selectedInsurances.length > 0 ? { scale: 1.02 } : {}}
                      whileTap={selectedInsurances.length > 0 ? { scale: 0.98 } : {}}
                    >
                      Fortsæt
                    </motion.button>
                  </motion.div>
                </motion.div>
              </main>
              <WhyChooseUs />
            </>
          );
      }
    })();

    return (
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      }>
        {content}
      </Suspense>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo onClick={() => navigateToPage('home')} />
          <div className="flex space-x-6 text-sm font-medium">
            <motion.button 
              onClick={() => navigateToPage('blogs')}
              className="text-gray-700 hover:text-green-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Blog
            </motion.button>
            <motion.button 
              onClick={() => navigateToPage('about')}
              className="text-gray-700 hover:text-green-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Om os
            </motion.button>
            <motion.button 
              onClick={() => navigateToPage('contact')}
              className="text-gray-700 hover:text-green-600 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Kontakt
            </motion.button>
          </div>
        </nav>
      </header>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentPage}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderContent()}
        </motion.div>
      </AnimatePresence>

      <footer className="bg-[#1E2530] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <h3 className="text-xl font-semibold mb-6">Kontakt</h3>
              <div className="space-y-2">
                <p>Birkhøjterrasserne 425F</p>
                <p>3520 Farum</p>
                <p>CVR: 45490432</p>
                <p>Telefon: +45 31 17 10 80</p>
                <p>Email: support@autoinsurance.dk</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-6">Links</h3>
              <div className="space-y-2">
                <motion.button 
                  onClick={() => navigateToPage('about')}
                  className="block text-white hover:text-gray-300 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  Om os
                </motion.button>
                <motion.button 
                  onClick={() => navigateToPage('privacy')}
                  className="block text-white hover:text-gray-300 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  Persondatapolitik
                </motion.button>
                <motion.button 
                  onClick={() => navigateToPage('terms')}
                  className="block text-white hover:text-gray-300 transition-colors"
                  whileHover={{ x: 5 }}
                >
                  Vilkår
                </motion.button>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-6">Om os</h3>
              <p className="text-gray-300 mb-6">
                Vi er Danmarks eneste platform, hvor du bliver kontaktet af selvstændige forsikringsformidlere fra dine valgte selskaber.
              </p>
              <motion.img 
                src="https://i.imgur.com/kGwlgVy.png" 
                alt="Footer Logo" 
                className="h-12"
                whileHover={{ scale: 1.05 }}
              />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;